import React, { FC, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { IPRMResults } from '../../hooks/usePerformanceRiskMonitor';
import DonutChart from '../DonutChart/DonutChart';
import NoData from '../NoData/NoData';
import PRMCategoryResultsTile from './PRMCategoryResultsTile/PRMCategoryResultsTile';
import {
  CategoryDesc,
  Close,
  DonutCont,
  DonutTitle,
  LeftSlideSideCont,
  OpenAll,
  PrmSlideCont,
  RightSlideSideCont,
  SideDescription,
  SideTitle,
} from './PRMSliderBody.styles';

interface IPRMSliderBody {
  data: IPRMResults;
  segmentCategory: { active: string; setActive: (code: string) => void };
  donutPartAction: (slideNr: number) => void;
  averages: Record<string, number>;
  group: string;
}

const PRMSliderBody: FC<IPRMSliderBody> = ({
  data,
  segmentCategory,
  donutPartAction,
  averages,
  group,
}) => {
  const { t } = useTranslation();

  const {
    chart,
    count,
    share,
    rating,
    workEnvironment: { categoryResults = [] } = {},
    segment: { description },
  } = data ?? {};
  const emptyInfoCatsCondition = categoryResults?.length > 0;
  const emptyInfoPeopleCondition = count?.current > 0;
  const emptyText =
    count.current === 0 ? t('report.prm.noEmployeesInGroup') : t('report.prm.noCategories');

  const renderCategoryDesc = useMemo(() => {
    const category = categoryResults?.filter(
      category => category.category.code === segmentCategory.active
    )[0];

    return (
      category && (
        <>
          <PRMCategoryResultsTile
            category={category}
            onClick={() => segmentCategory.setActive('')}
            icon={<Close />}
            isActive={true}
            average={averages[category.category.code]}
            group={group}
          />
          <CategoryDesc
            data-testid="categoryDesc"
            id="CategoryDesc"
            dangerouslySetInnerHTML={{ __html: category.category.description || '' }}
          />
        </>
      )
    );
  }, [categoryResults, segmentCategory]);

  const renderCategoryResultTable = useMemo(() => {
    return _.isEmpty(categoryResults) ? (
      <NoData message={t('report.prm.noEmployeesInGroup')} />
    ) : (
      categoryResults?.map(category => (
        <PRMCategoryResultsTile
          key={category.category.code}
          category={category}
          onClick={() => segmentCategory.setActive(category.category.code)}
          icon={<OpenAll />}
          isActive={false}
          average={averages[category.category.code]}
          group={group}
        />
      ))
    );
  }, [categoryResults, segmentCategory, t]);

  const renderPerceivedWorkEnvironment = useMemo(() => {
    return _.isEmpty(segmentCategory.active) ? renderCategoryResultTable : renderCategoryDesc;
  }, [renderCategoryDesc, renderCategoryResultTable, segmentCategory.active]);

  return (
    <PrmSlideCont>
      <LeftSlideSideCont>
        <SideTitle>{t('report.prm.groupProportion')}</SideTitle>
        <DonutCont>
          <DonutChart
            colors={chart.colors}
            count={count.current}
            data={chart.data}
            dataKey="value"
            share={share.current}
            onClick={event => donutPartAction(Number((event.target as SVGGElement).id))}
          />
        </DonutCont>
        <DonutTitle>
          {rating
            ? rating?.description.replaceAll('<br>', '') || ''
            : t('report.prm.noEmployeesInGroup')}
        </DonutTitle>
        <SideDescription>
          <div dangerouslySetInnerHTML={{ __html: description || '' }} />
        </SideDescription>
      </LeftSlideSideCont>
      <RightSlideSideCont>
        <SideTitle>{t('report.prm.perceivedWorkEnvironment')}</SideTitle>
        {emptyInfoCatsCondition && emptyInfoPeopleCondition ? (
          renderPerceivedWorkEnvironment
        ) : (
          <NoData message={emptyText} />
        )}
      </RightSlideSideCont>
    </PrmSlideCont>
  );
};

export default PRMSliderBody;
